import React, { useRef } from "react";
import { Box, Typography, Paper, Stack, useTheme, useMediaQuery } from "@mui/material";
import { motion, useInView } from "framer-motion";
import { COLORS } from "constants/colors";

const steps = [
  {
    number: "1",
    title: "Upload Your Data",
    description: "Import datasets from CSV, Excel, SPSS, or SQL in seconds.",
    image: "/lovable-uploads/upload.png",
  },
  {
    number: "2",
    title: "Automatic Data Validation",
    description: "Genie checks for missing values, outliers, and structure issues.",
    image: "/lovable-uploads/validation.png",
  },
  {
    number: "3",
    title: "Ask for Analysis",
    description: "Pick a suggested question or type your own analysis prompt.",
    image: "/lovable-uploads/ask.png",
  },
  {
    number: "4",
    title: "Instant Results",
    description: "Genie produces formatted tables, charts, and full conclusions instantly.",
    image: "/lovable-uploads/tables.png",
  },
];

const WizardSteps = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getCardDimensions = () => {
    if (isSmallMobile) {
      return { width: "100%", height: 280 };
    } else if (isMobile) {
      return { width: 280, height: 300 };
    }
    return { width: 300, height: 320 };
  };

  const { width: cardWidth, height: cardHeight } = getCardDimensions();

  return (
    <Stack
      ref={ref}
      component="section"
      sx={{
        backgroundColor: "#ffffff",
        py: { xs: 6, md: 10 },
        px: { xs: "24px", md: 6 },
        position: "relative",
        boxSizing: "border-box",
        gap: "10px",
      }}
    >
      {/* Title and subtitle */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
      >
        <Stack>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            textAlign="center"
            color="#1A1A1A"
            mb={2}
            sx={{ px: { xs: 1, sm: 0 } }}
          >
            Built-In Wizard, Tailored Insights
          </Typography>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            color={COLORS.gray900}
            mb={{ xs: 4, md: 6 }}
            sx={{
              fontWeight: 400,
              maxWidth: 800,
              mx: "auto",
              lineHeight: 1.5,
              textAlign: "center",
              px: { xs: 1, sm: 0 },
            }}
          >
            Genie's intuitive wizard guides you through the analysis process, offering tailored suggestions
            based on your data, delivering actionable insights in just a few clicks!
          </Typography>
        </Stack>
      </motion.div>

      {/* Responsive grid container */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: `repeat(2, ${cardWidth}px)`,
          },
          gridAutoRows: {
            xs: `${cardHeight}px`,
            sm: `${cardHeight}px`,
            md: `${cardHeight}px`,
          },
          justifyContent: "center",
          gap: { xs: 7, md: 6 },
          boxSizing: "border-box",
          width: "100%",
          maxWidth: { xs: "100%", md: "auto" },
        }}
      >
        {steps.map((step, index) => (
          <Box
            key={step.number}
            sx={{
              position: "relative",
              width: "100%",
              height: cardHeight,
              boxSizing: "border-box",
              justifySelf: "center",
              maxWidth: { xs: "100%", sm: cardWidth, md: cardWidth },
            }}
          >
            {/* Step number always visible */}
            <Box
              sx={{
                position: "absolute",
                top: -20,
                left: "50%",
                transform: "translateX(-50%)",
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                backgroundColor: COLORS.purpleDark,
                color: "#fff",
                fontWeight: "bold",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 3,
                zIndex: 2,
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              {step.number}
            </Box>

            {/* Animated card below */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: isMobile ? 1.02 : 1.05 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={{ width: "100%", height: "100%" }}
            >
              <Paper
                elevation={4}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "300px", sm: "100%" },
                  mx: "auto",
                  height: "100%",
                  pt: { xs: 4, sm: 5 },
                  pb: { xs: 2, sm: 3 },
                  px: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  backgroundColor: COLORS.cardsBackground,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                <Box
                  component="img"
                  src={step.image}
                  alt={step.title}
                  sx={{
                    width: "100%",
                    maxHeight: { xs: 80, sm: 100 },
                    objectFit: "contain",
                    mt: { xs: 1, sm: 2 },
                    mb: 2,
                  }}
                />
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  fontWeight="bold"
                  mb={{ xs: 1, sm: 2 }}
                  sx={{ fontSize: "18px" }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    //fontSize: { xs: "13px", sm: "14px" },
                    lineHeight: 1.4,
                  }}
                >
                  {step.description}
                </Typography>
              </Paper>
            </motion.div>
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

export default WizardSteps;
